import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Nummerierung, NummerierungEingabe } from "@kundeninfo/api-client";
import {
  darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload, useBestaetigung, useFormularZustand,
} from "@kundeninfo/ui";
import { Chip, Formular, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { Meldung, meldungAus } from "./gemeinsam.js";
import { KiKnopf } from "../../components/meHuellen.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich NUMMERIERUNG (Spec „Überschriften-Nummerierung", Migration 048, Dirk 254): benannte
 * Schemata für die Überschriften der Belegtexte, je Ebene ein Formatstring nach dem FrameMaker-4-Modell.
 *
 * **Ohne zweite Ebene** — Muster „Teams": Name, sechs Formate und das Beispiel stehen in der Zeile; angelegt und
 * geändert wird im strikten Modal, gelöscht nach gestapelter Bestätigung (Hausregel 19.10.). Die beiden eingebauten
 * Schemata (Hierarchisch, Juristisch) stehen oben, schreibgeschützt.
 *
 * **Hilfetext am Feld (Dirk 254):** Jedes der sechs Formatfelder nennt die Bausteine; darüber steht die Bausteintabelle
 * der Notation (md-renderer `docs/notation-nummerierung.md`).
 * Beim Tippen prüft der Server die Formate (`probeNumberingScheme`, 300 ms Ruhe) und liefert das Beispiel der festen
 * Folge H1 H2 H3 H3 H4 H5 H6 H2 H1 — `web` darf `domain` nicht einführen.
 */

export type NummerierungClient = Pick<ApiClient,
  "listNumberingSchemes" | "createNumberingScheme" | "updateNumberingScheme" | "deleteNumberingScheme"
  | "probeNumberingScheme">;

type Formate = NummerierungEingabe["formate"];
type Ebene = keyof Formate;
const EBENEN: readonly Ebene[] = ["h1", "h2", "h3", "h4", "h5", "h6"];
const LEER: Formate = { h1: "", h2: "", h3: "", h4: "", h5: "", h6: "" };

/** Die Kurzhilfe an JEDEM Formatfeld (Dirk 254: „welche Schlüsselelemente unterstützt werden"). */
export const FORMAT_HILFE = "Bausteine: <n+> zählt hoch · <n> zeigt · <n=1> setzt · < > hält ohne Anzeige · "
  + "< =0> setzt ohne Anzeige · <a+> <A+> <aa+> Buchstaben · <r+> <R+> römisch · „X:“ am Anfang = eigene Reihe";

/** Die Bausteintabelle im Modal — wortgleich mit md-renderer `docs/notation-nummerierung.md`, Abschnitt
 *  „Bausteine“ (Baustein, Wirkung, Anzeige). Ändert sich die Notation, wird diese Tabelle mitgezogen. */
export const BAUSTEIN_TABELLE: readonly (readonly [string, string, string])[] = [
  ["<n+>", "Position erhöhen", "1, 2, 3"],
  ["<n>", "Position zeigen, unverändert", "1, 2, 3"],
  ["<n=1> (allgemein <n=k>, k bis 9999)", "Position setzen", "k"],
  ["< >", "Position halten", "nichts"],
  ["< =0> (allgemein < =k>)", "Position setzen", "nichts"],
  ["<a+>, <a>, <a=k>", "wie oben", "a, b, c … z, aa, ab"],
  ["<A+>, <A>, <A=k>", "wie oben", "A, B, C"],
  ["<aa+>, <aaa+>, auch <AA+>, <AAA+> (ebenso ohne + oder mit =k)", "Buchstabe wiederholt", "aa, bb, cc · aaa, bbb"],
  ["<r+>, <r>, <r=k>", "wie oben", "i, ii, iii"],
  ["<R+>, <R>, <R=k>", "wie oben", "I, II, III"],
  ["X: am Anfang (ein Buchstabe)", "eigene Zählerreihe X", "—"],
];

type Dialog = { id: string | null; version: number; name: string; formate: Formate };
type DialogFeld = "name" | "formatH1" | "formatH2" | "formatH3" | "formatH4" | "formatH5" | "formatH6";
const DIALOG_FELDER: readonly DialogFeld[] = ["name", "formatH1", "formatH2", "formatH3", "formatH4", "formatH5",
                                              "formatH6"];

function dialogAus(s: Nummerierung | null): Dialog {
  return s === null ? { id: null, version: 0, name: "", formate: LEER }
    : { id: s.id, version: s.version, name: s.name, formate: { ...s.formate } };
}

function dialogGleich(a: Dialog, b: Dialog): boolean {
  return a.id === b.id && a.version === b.version && a.name === b.name
    && EBENEN.every((h) => a.formate[h] === b.formate[h]);
}

/** „§ 1 · (1) · 1. · 2. · a) · aa) · – · (2) · § 2" — leere Nummern als Gedankenstrich. */
export function beispielZeile(beispiel: readonly { ebene: number; nummer: string }[]): string {
  return beispiel.map((b) => (b.nummer === "" ? "–" : b.nummer)).join(" · ");
}

export function NummerierungBereich({ client }: { client: NummerierungClient }): ReactElement {
  const [zeilen, setZeilen] = useState<Nummerierung[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [probe, setProbe] = useState<{ fehler: Record<string, string>; beispiel: string | null } | null>(null);
  const [auskunft, setAuskunft] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();
  const probeNr = useRef(0);

  const listNumberingSchemes = client.listNumberingSchemes;
  useEffect(() => {
    let abgebrochen = false;
    listNumberingSchemes()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) setLadeFehler(meldungAus(fehler, "Die Nummerierungsschemata konnten nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [listNumberingSchemes]);

  // PROBE beim Tippen: 300 ms Ruhe, nur die jüngste Antwort zählt.
  const formateSchluessel = dialog === null ? null : JSON.stringify(dialog.formate);
  const probeNumberingScheme = client.probeNumberingScheme;
  useEffect(() => {
    if (formateSchluessel === null) { setProbe(null); return; }
    const nr = ++probeNr.current;
    const uhr = window.setTimeout(() => {
      probeNumberingScheme(JSON.parse(formateSchluessel) as Formate)
        .then((a) => {
          if (nr !== probeNr.current) return;
          setProbe({ fehler: Object.fromEntries(a.fehler.map((f) => [f.field, f.message])),
                     beispiel: a.beispiel === null ? null : beispielZeile(a.beispiel) });
        })
        .catch(() => { if (nr === probeNr.current) setProbe(null); });
    }, 300);
    return () => window.clearTimeout(uhr);
  }, [formateSchluessel, probeNumberingScheme]);

  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Schema offen.");
      const eingabe: NummerierungEingabe = { name: d.name, formate: d.formate };
      if (d.id === null) await client.createNumberingScheme(eingabe);
      else await client.updateNumberingScheme(d.id, d.version, eingabe);
      try {
        setZeilen((await client.listNumberingSchemes()).items);
      } catch (fehler: unknown) {
        setLadeFehler(meldungAus(fehler, "Die Nummerierungsschemata konnten nicht geladen werden."));
      }
      return true;
    },
    neuLaden: async () => {
      const antwort = await client.listNumberingSchemes();
      setZeilen(antwort.items);
      const d = dialog;
      if (d === null || d.id === null) return;
      const frisch = antwort.items.find((s) => s.id === d.id);
      if (frisch === undefined) {
        setDialog(null);
        setAuskunft("Dieses Schema gibt es nicht mehr.");
        return;
      }
      setDialog(dialogAus(frisch));
    },
    bekannteFelder: DIALOG_FELDER,
    beschriftungen: { name: "Name", formatH1: "Ebene 1", formatH2: "Ebene 2", formatH3: "Ebene 3",
                      formatH4: "Ebene 4", formatH5: "Ebene 5", formatH6: "Ebene 6" },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<DialogFeld, string>>;

  const dialogGeaendert = dialog !== null && !dialogGleich(
    dialog, dialogAus(zeilen?.find((s) => s.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  async function abbrechen(): Promise<void> {
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  function oeffnen(s: Nummerierung | null): void {
    formular.zuruecksetzen();
    setAuskunft(null);
    setDialog(dialogAus(s));
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) setDialog(null);
  }

  async function loeschen(s: Nummerierung): Promise<void> {
    if (!(await bestaetigen({ titel: `„${s.name}“ löschen?`, bestaetigenText: "Löschen", gefaehrlich: true,
      text: "Textblöcke, die das Schema im Front-Matter nennen, erscheinen danach ohne Nummern." }))) return;
    try {
      await client.deleteNumberingScheme(s.id, s.version);
      setAuskunft(`„${s.name}“ gelöscht.`);
    } catch (fehler: unknown) {
      setAuskunft(meldungAus(fehler, "Das Schema konnte nicht gelöscht werden."));
    }
    try {
      setZeilen((await client.listNumberingSchemes()).items);
    } catch (fehler: unknown) {
      setLadeFehler(meldungAus(fehler, "Die Nummerierungsschemata konnten nicht geladen werden."));
    }
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  return (
    <>
      {auskunft !== null && <Hinweis art="info">{auskunft}</Hinweis>}
      <p className="vw__hinweis">
        Ein Schema nummeriert die Überschriften der Belegtexte (Einleitung, Ausleitung, Anlagen). Gewählt wird es je
        Layout im Belegdesigner (Register „Texte“); ein Textblock kann im Front-Matter mit „nummerierung: neu“, „aus“
        oder einem Schemanamen abweichen.
      </p>
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => oeffnen(null)}>
          Neues Schema
        </KiKnopf>
      </div>
      <ul className="vw__liste">
        {zeilen.map((s) => (
          <li key={s.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{s.name}</span>
              {s.eingebaut && <Chip className="vw__marke">eingebaut</Chip>}
            </div>
            <span className="vw__eintrag-zusatz">{beispielZeile(s.beispiel)}</span>
            {s.verwendetIn.length > 0 && (
              <span className="vw__eintrag-zusatz">Gewählt in: {s.verwendetIn.join(", ")}</span>
            )}
            {!s.eingebaut && (
              <div className="vw__eintrag-aktionen">
                <KiKnopf name={`${s.name} ändern`} onClick={() => oeffnen(s)}>Ändern</KiKnopf>
                <KiKnopf name={`${s.name} löschen`} onClick={() => { void loeschen(s); }}>Löschen</KiKnopf>
              </div>
            )}
          </li>
        ))}
      </ul>

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neues Nummerierungsschema" : "Nummerierungsschema ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          <Hinweis art="info">
            Je Ebene ein Format aus Text und Bausteinen. Die Bausteine wirken von links nach rechts auf die
            Zählerpositionen 1, 2, 3 …; Text dazwischen erscheint wörtlich. Eine leere Ebene bleibt ohne Nummer.
          </Hinweis>
          <Tabelle dicht>
            <table aria-label="Bausteine der Formate">
              <thead>
                <tr>
                  <th scope="col">Baustein</th>
                  <th scope="col">Wirkung</th>
                  <th scope="col">Anzeige</th>
                </tr>
              </thead>
              <tbody>
                {BAUSTEIN_TABELLE.map(([baustein, wirkung, anzeige]) => (
                  <tr key={baustein}>
                    <td><code>{baustein}</code></td>
                    <td>{wirkung}</td>
                    <td>{anzeige}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Tabelle>
          <p className="vw__hinweis">
            Wer eine Position erhöht oder setzt, stellt alle Positionen rechts davon auf 0 — darum beginnt unter jedem
            § die Absatzzählung neu. Eine Reihe ist genau ein Buchstabe mit Doppelpunkt am Anfang („B:Bild &lt;n+&gt;“).
            Nicht erlaubt sind ` * _ [ ] \ $ | # {"{"} {"}"} und Steuerzeichen, höchstens 120 Zeichen je Ebene.
            Beispiel Juristisch, Ebene 2: &lt; &gt;(&lt;n+&gt;) ergibt „(1)“.
          </p>
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Dieses Schema wurde inzwischen geändert. Deine Eingaben stehen noch da — „Neu laden" übernimmt den
                aktuellen Stand.
              </span>
            ) : undefined}>
            <Feld id="vw-nr-name" label="Name" bearbeitbar wert={dialog.name} max={60}
              hilfe="So heißt das Schema im Belegdesigner und im Front-Matter („nummerierung: Name“)."
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
              {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
            {EBENEN.map((h, i) => {
              const feld = `formatH${i + 1}` as DialogFeld;
              const fehler = feldFehler[feld] ?? probe?.fehler[feld];
              return (
                <Feld key={h} id={`vw-nr-${h}`} label={`Ebene ${i + 1} (${"#".repeat(i + 1)})`} bearbeitbar
                  wert={dialog.formate[h]} max={120} hilfe={FORMAT_HILFE}
                  onChange={(w) => setDialog((d) => (d === null ? d : { ...d, formate: { ...d.formate, [h]: w } }))}
                  {...(fehler !== undefined ? { fehler } : {})} />
              );
            })}
          </Formular>
          {probe?.beispiel != null && (
            <p className="vw__hinweis" aria-live="polite">Beispiel: {probe.beispiel}</p>
          )}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
